import React from 'react';
import styled, { CSSProp } from 'styled-components';
import { themeGet as get } from '@styled-system/theme-get';
import {
  color,
  width,
  maxWidth,
  textAlign,
  fontSize,
  fontFamily,
  lineHeight,
  fontWeight,
  display,
  space,
  ColorProps,
  FlexboxProps,
  LayoutProps,
  SpaceProps,
  TextAlignProps,
  TypographyProps,
} from 'styled-system';

type TitleSize = 'hero' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type TitleProps = {
  as?: React.ElementType;
  size?: TitleSize | (string & {}) | number;
  textWrap?: 'balance' | 'truncate' | 'wrap';
  children?: React.ReactNode;
  css?: CSSProp;
} & Omit<React.HTMLAttributes<HTMLElement>, 'color'> &
  ColorProps &
  SpaceProps &
  TextAlignProps &
  TypographyProps &
  LayoutProps &
  FlexboxProps;

const fontSizeDefault = (props) => {
  const { size } = props;
  const fsHead = 'fontSizes.heading';
  const mobile = get(`${fsHead}.mobile.default`)(props);
  const setValue = () => {
    switch (size) {
      case 'hero':
        return [mobile + 12, get(`${fsHead}.hero`)(props)];
      case 'h1':
        return [mobile + 10, get(`${fsHead}.h1`)(props)];
      case 'h2':
        return [mobile, get(`${fsHead}.h2`)(props)];
      case 'h3':
        return [mobile, get(`${fsHead}.h3`)(props)];
      case 'h4':
        return [mobile, get(`${fsHead}.h4`)(props)];
      case 'h5':
        return [mobile, get(`${fsHead}.h5`)(props)];
      default:
        return mobile;
    }
  };
  return {
    fontSize: setValue(),
    ...props,
  };
};

const lineHeightDefault = (props) => {
  const { size } = props;
  const lhHead = 'lineHeights.heading';
  const mobile = get(`${lhHead}.mobile.default`)(props);
  const setValue = () => {
    switch (size) {
      case 'hero':
      case 'h1':
        return [mobile, get(`${lhHead}.h1`)(props)];
      case 'h2':
        return [mobile, get(`${lhHead}.h2`)(props)];
      case 'h3':
        return [mobile, get(`${lhHead}.h3`)(props)];
      case 'h4':
        return [mobile, get(`${lhHead}.h4`)(props)];
      case 'h5':
        return [mobile, get(`${lhHead}.h5`)(props)];
      default:
        return mobile;
    }
  };
  return {
    lineHeight: setValue(),
    ...props,
  };
};

const marginDefault = ({ size, m, mt, mb, ...props }: TitleProps) => {
  const setValue = () => {
    switch (size) {
      case 'hero':
      case 'h1':
        return 'xxl';
      case 'h2':
      case 'h3':
        return 'l';
      case 'h4':
      case 'h6':
        return 's';
      default:
        return 'm';
    }
  };
  return {
    m: m !== undefined ? m : null,
    mt: m === undefined ? mt : null,
    mb: m === undefined ? mb || setValue() : null,
    ...props,
  };
};

const StyledTitle = styled.h2<TitleProps>`
  ${display};
  ${width};
  ${maxWidth};
  ${color};
  ${textAlign};
  ${fontWeight};
  ${fontFamily};
  ${(props) => lineHeight(lineHeightDefault(props))};
  ${(props) => fontSize(fontSizeDefault(props))};
  ${(props) => space(marginDefault(props))};
  ${(props) => `text-wrap: ${props.textWrap};`};
`;

const Title = ({
  as = 'h2',
  size = 'h2',
  fontFamily: titleFamily = 'headings',
  fontWeight: titleWeight = 400,
  color: titleColor = 'brand.blue',
  mt = 0,
  textWrap = 'balance',
  ...props
}: TitleProps) => (
  <StyledTitle
    as={as}
    size={size}
    fontFamily={titleFamily}
    fontWeight={titleWeight}
    color={titleColor}
    mt={mt}
    textWrap={textWrap}
    {...props}
  />
);

export default Title;
